import { useAppSelector } from "@/app/hooks";
import { isShortlistEnabled } from "@/shared/utils/propertyUtils";
import { EmeraldSwitch } from "@emerald-react/switch";
import { EmeraldTooltip } from "@emerald-react/tooltip";
import { useEffect } from "react";

interface ShortlistFilterToggleProps {
  onToggle: (showShortlistOnly: boolean) => void;
  shortlistedCount: number;
  disabled?: boolean;
  className?: string;
}

/**
 * Reusable Shortlist Filter Toggle Component
 *
 * Displays as a toggle switch
 * Automatically disables when there are no shortlisted properties
 */
const ShortlistFilterToggle = ({
  onToggle,
  shortlistedCount,
}: ShortlistFilterToggleProps) => {
  const { currentSurvey, currentSurveyGroup, mapFilters } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const { showShortlistOnly } = mapFilters;
  const shortlistMessage =
    "Once you begin shortlisting you can filter your view to show only shortlisted properties. You can review these with your brokerage team.";

  // Don't render if shortlist is disabled for this survey/group
  if (!isShortlistEnabled(currentSurvey, currentSurveyGroup)) {
    return null;
  }

  const isDisabled = shortlistedCount === 0;

  const handleToggle = () => {
    if (!isDisabled) {
      window.sessionStorage.setItem(
        "shortlisted",
        JSON.stringify(!showShortlistOnly),
      );
      onToggle(!showShortlistOnly);
    }
  };

  useEffect(() => {
    if (shortlistedCount === 0 && showShortlistOnly) {
      window.sessionStorage.setItem("shortlisted", "false");
      onToggle(false);
    }
  }, [shortlistedCount]);

  useEffect(() => {
    if (showShortlistOnly) {
      onToggle(showShortlistOnly);
    }
  }, [showShortlistOnly]);

  return (
    <div className="flex items-center">
      <label
        className={`inline-flex pr-2 items-center ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
      >
        <EmeraldTooltip
          triggerElement={
            <span
              className="info-i"
              style={{ margin: "0 8px 12px 0" }}
              aria-label={shortlistMessage}
            >
              i
            </span>
          }
        >
          {shortlistMessage}
        </EmeraldTooltip>
        <span className="shortlist-toggle-label">Shortlisted</span>
        <div className="relative pr-2">
          <EmeraldSwitch
            selected={showShortlistOnly}
            onChange={handleToggle}
            disabled={isDisabled}
            className="shortlist-toggle-switch"
          />
        </div>
      </label>
    </div>
  );
};

export default ShortlistFilterToggle;
